import React, { useState, useEffect } from 'react';
import './App.css';
import {
  CubeTransparentIcon,
  ShieldCheckIcon,
  ScaleIcon,
  ArrowRightIcon,
  CogIcon,
  UserGroupIcon,
  ChartBarIcon,
  ArrowLeftEndOnRectangleIcon,
  ArrowPathIcon,
  LinkIcon,
  PlusIcon,
  BoltIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Login from './components/Login';
import { platformApi, ServiceStatus } from './services/platformApi';
import { connectorApi } from './services/connectorApi';
import ConnectorDashboard from './components/ConnectorDashboard';
import ConnectorWizard from './components/ConnectorWizard';
import JobMonitor from './components/JobMonitor';
import SettingsManager from './components/SettingsManager';
import UserManager from './components/UserManager';
import AgentDashboard from './components/AgentDashboard';

interface Domain extends ServiceStatus {
  icon: React.ComponentType<any>;
  color: string;
}

const domainConfigs = [
  {
    id: 'declass',
    icon: ShieldCheckIcon,
    color: 'blue'
  },
  {
    id: 'legal',
    icon: ScaleIcon,
    color: 'green'
  }
];

type NavigationTab = 'dashboard' | 'connectors' | 'jobs' | 'agents' | 'settings' | 'users';

const Dashboard: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const [activeTab, setActiveTab] = useState<NavigationTab>('dashboard');
  const [showConnectorWizard, setShowConnectorWizard] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [connectorRefreshTrigger, setConnectorRefreshTrigger] = useState(0);
  const { user, logout } = useAuth();

  // Load and refresh service status
  const refreshServices = async () => {
    setIsRefreshing(true);
    try {
      const serviceStatuses = await platformApi.checkAllServicesHealth();
      
      // Merge with domain configs
      const domainsWithStatus: Domain[] = domainConfigs.map(config => {
        const status = serviceStatuses.find(s => s.id === config.id);
        return {
          ...config,
          ...(status || {
            id: config.id,
            name: config.id,
            description: 'Service status unknown',
            url: '#',
            status: 'offline' as const,
            lastChecked: new Date()
          })
        };
      });
      
      setDomains(domainsWithStatus);
      setLastRefresh(new Date());
    } catch (error) {
      console.error('Failed to refresh services:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  // Load services on mount and set up auto-refresh
  useEffect(() => {
    refreshServices();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshServices, 30000);
    return () => clearInterval(interval);
  }, []);

  // Handle token from URL (when returning from a domain)
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes('token=')) {
      const urlToken = hash.split('token=')[1].split('&')[0];
      if (urlToken) {
        localStorage.setItem('authToken', urlToken);
        window.location.hash = '';
      }
    }
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-500';
      case 'offline': return 'text-red-500';
      case 'starting': return 'text-yellow-500';
      default: return 'text-gray-500';
    }
  };

  const getDomainColorClasses = (color: string) => {
    switch (color) {
      case 'blue': return 'border-blue-200 bg-blue-50 hover:bg-blue-100';
      case 'green': return 'border-green-200 bg-green-50 hover:bg-green-100';
      case 'red': return 'border-red-200 bg-red-50 hover:bg-red-100';
      case 'purple': return 'border-purple-200 bg-purple-50 hover:bg-purple-100';
      case 'indigo': return 'border-indigo-200 bg-indigo-50 hover:bg-indigo-100';
      case 'teal': return 'border-teal-200 bg-teal-50 hover:bg-teal-100';
      default: return 'border-gray-200 bg-gray-50 hover:bg-gray-100';
    }
  };

  const handleDomainSelect = (domain: Domain) => {
    if (domain.status === 'online') {
      try {
        // Get the dynamic service URL
        const serviceUrl = platformApi.getServiceUrl(domain.id);
        
        // Pass the auth token to the domain application via URL hash
        const tokenParam = `#token=${localStorage.getItem('authToken')}`;
        window.open(serviceUrl + tokenParam, '_blank');
      } catch (error) {
        console.error('Failed to get service URL:', error);
        alert(`Failed to open ${domain.name}. Please check the service configuration.`);
      }
    } else {
      alert(`${domain.name} is currently ${domain.status}. Please wait for the service to start or check the deployment.`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <CubeTransparentIcon className="h-8 w-8 text-indigo-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Cognitive Fabric</h1>
              <span className="ml-3 px-2 py-1 text-xs font-semibold bg-indigo-100 text-indigo-800 rounded-full">
                Admin Dashboard
              </span>
            </div>
            <div className="flex items-center space-x-4">
              {lastRefresh && (
                <div className="text-xs text-gray-500">
                  Last refresh: {lastRefresh.toLocaleTimeString()}
                </div>
              )}
              <button
                onClick={refreshServices}
                disabled={isRefreshing}
                className="flex items-center text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50 transition-colors"
                title="Refresh service status"
              >
                <ArrowPathIcon className={`h-4 w-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
                Refresh
              </button>
              <span className="text-sm text-gray-700">{user?.full_name || user?.username}</span>
              <span className="text-xs text-gray-500">{user?.role}</span>
              <button
                onClick={logout}
                className="flex items-center text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <ArrowLeftEndOnRectangleIcon className="h-4 w-4 mr-1" />
                Logout
              </button>
            </div>
          </div>
          
          {/* Navigation Tabs */}
          <div className="border-t border-gray-200">
            <nav className="-mb-px flex space-x-8">
              {[
                { id: 'dashboard', label: 'Dashboard', icon: ChartBarIcon },
                { id: 'connectors', label: 'Data Connectors', icon: LinkIcon },
                { id: 'jobs', label: 'Processing Jobs', icon: CogIcon },
                { id: 'agents', label: 'Agent System', icon: BoltIcon },
                { id: 'settings', label: 'Settings', icon: CogIcon },
                { id: 'users', label: 'Users', icon: UserGroupIcon }
              ].map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id as NavigationTab)}
                    className={`${
                      activeTab === tab.id
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm flex items-center`}
                  >
                    <Icon className="h-5 w-5 mr-2" />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {activeTab === 'dashboard' && (
          <>
            {/* Welcome Section */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Cognitive Fabric</h2>
              <p className="text-gray-600">
                Multi-domain document intelligence platform. Select a domain application to begin working.
              </p>
            </div>

        {/* Platform Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <ChartBarIcon className="h-8 w-8 text-blue-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Active Domains</p>
                <p className="text-2xl font-bold text-gray-900">
                  {domains.filter(d => d.status === 'online').length}
                </p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <UserGroupIcon className="h-8 w-8 text-green-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Domains</p>
                <p className="text-2xl font-bold text-gray-900">{domains.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <CogIcon className="h-8 w-8 text-yellow-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Platform Status</p>
                <p className="text-2xl font-bold text-green-600">Healthy</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-6 rounded-lg shadow">
            <div className="flex items-center">
              <ShieldCheckIcon className="h-8 w-8 text-indigo-500" />
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">API Status</p>
                <p className="text-2xl font-bold text-green-600">Online</p>
              </div>
            </div>
          </div>
        </div>

        {/* Domain Applications */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Domain Applications</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {domains.map((domain) => {
              const IconComponent = domain.icon;
              return (
                <div
                  key={domain.id}
                  className={`relative p-6 rounded-lg border-2 cursor-pointer transition-all duration-200 ${getDomainColorClasses(domain.color)}`}
                  onClick={() => handleDomainSelect(domain)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <IconComponent className="h-10 w-10 text-gray-700" />
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${domain.status === 'online' ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className={`text-sm font-medium ${getStatusColor(domain.status)}`}>
                        {domain.status}
                      </span>
                    </div>
                  </div>
                  
                  <h4 className="text-lg font-semibold text-gray-900 mb-2">{domain.name}</h4>
                  <p className="text-sm text-gray-600 mb-3">{domain.description}</p>
                  
                  <div className="space-y-1 mb-3">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>URL:</span>
                      <span className="font-mono truncate ml-2">{domain.url}</span>
                    </div>
                    {domain.responseTime && (
                      <div className="flex items-center justify-between text-xs text-gray-500">
                        <span>Response:</span>
                        <span>{domain.responseTime}ms</span>
                      </div>
                    )}
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Last Check:</span>
                      <span>{domain.lastChecked.toLocaleTimeString()}</span>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className={`text-xs font-medium ${domain.status === 'online' ? 'text-green-600' : 'text-red-600'}`}>
                      {domain.status === 'online' ? 'Click to Open' : 'Service Unavailable'}
                    </span>
                    <ArrowRightIcon className="h-5 w-5 text-gray-400" />
                  </div>
                  
                  {domain.status === 'online' && (
                    <div className="absolute top-2 right-2">
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <button 
              onClick={() => setActiveTab('settings')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 text-left"
            >
              <CogIcon className="h-6 w-6 text-blue-500 mb-2" />
              <h5 className="font-medium text-gray-900">Platform Settings</h5>
              <p className="text-xs text-gray-500">Configure global platform settings</p>
            </button>
            
            <button 
              onClick={() => setActiveTab('users')}
              className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 text-left"
            >
              <UserGroupIcon className="h-6 w-6 text-green-500 mb-2" />
              <h5 className="font-medium text-gray-900">User Management</h5>
              <p className="text-xs text-gray-500">Manage users and permissions</p>
            </button>
            
            <button className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 text-left">
              <ChartBarIcon className="h-6 w-6 text-yellow-500 mb-2" />
              <h5 className="font-medium text-gray-900">Analytics</h5>
              <p className="text-xs text-gray-500">View platform usage analytics</p>
            </button>
            
            <button className="p-4 bg-white rounded-lg shadow hover:shadow-md transition-shadow duration-200 text-left">
              <ShieldCheckIcon className="h-6 w-6 text-red-500 mb-2" />
              <h5 className="font-medium text-gray-900">Security</h5>
              <p className="text-xs text-gray-500">Security settings and logs</p>
            </button>
          </div>
        </div>

            {/* Recent Activity */}
            <div>
              <h3 className="text-xl font-semibold text-gray-900 mb-6">Recent Platform Activity</h3>
              <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200">
                  <h4 className="font-medium text-gray-900">System Events</h4>
                </div>
                <div className="divide-y divide-gray-200">
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">LegalFabric domain started</p>
                        <p className="text-xs text-gray-500">Port 3002 is now accepting connections</p>
                      </div>
                      <span className="text-xs text-gray-400">2 minutes ago</span>
                    </div>
                  </div>
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">User admin logged in</p>
                        <p className="text-xs text-gray-500">Authentication successful</p>
                      </div>
                      <span className="text-xs text-gray-400">5 minutes ago</span>
                    </div>
                  </div>
                  <div className="px-6 py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">Platform backend initialized</p>
                        <p className="text-xs text-gray-500">API server started on port 8000</p>
                      </div>
                      <span className="text-xs text-gray-400">10 minutes ago</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}

        {activeTab === 'connectors' && (
          <>
            {showConnectorWizard ? (
              <ConnectorWizard
                mode="create"
                onCancel={() => setShowConnectorWizard(false)}
                onSave={async (config) => {
                  try {
                    // Add required fields that the API expects
                    const connectorData = {
                      ...config,
                      user_id: user?.id || user?.username || 'current_user',
                      metadata_storage: config.metadata_storage || 'source_tags'
                    };
                    
                    console.log('Saving connector:', connectorData);
                    
                    // Call the API to create the connector
                    const createdConnector = await connectorApi.createConnector(connectorData);
                    console.log('Connector created successfully:', createdConnector);
                    
                    setShowConnectorWizard(false);
                    // Trigger a refresh of the connector list
                    setConnectorRefreshTrigger(prev => prev + 1);
                  } catch (error) {
                    console.error('Failed to save connector:', error);
                    throw error;
                  }
                }}
              />
            ) : (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <div>
                    <h2 className="text-3xl font-bold text-gray-900">Data Connectors</h2>
                    <p className="text-gray-600">Connect and manage external data sources for AI processing</p>
                  </div>
                  <button
                    onClick={() => setShowConnectorWizard(true)}
                    className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
                  >
                    <PlusIcon className="h-5 w-5 mr-2" />
                    New Connector
                  </button>
                </div>
                <ConnectorDashboard
                  onCreateNew={() => setShowConnectorWizard(true)}
                  onEdit={(jobId) => {
                    // TODO: Implement edit functionality
                    console.log('Edit job:', jobId);
                  }}
                  onViewDetails={(jobId) => {
                    setSelectedJobId(jobId);
                    setActiveTab('jobs');
                  }}
                  refreshTrigger={connectorRefreshTrigger}
                />
              </div>
            )}
          </>
        )}

        {activeTab === 'jobs' && (
          <div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Processing Jobs</h2>
              <p className="text-gray-600">Monitor and manage document processing jobs</p>
            </div>
            {selectedJobId ? (
              <div>
                <button
                  onClick={() => setSelectedJobId(null)}
                  className="mb-4 text-indigo-600 hover:text-indigo-800"
                >
                  ← Back to job list
                </button>
                <JobMonitor jobId={selectedJobId} />
              </div>
            ) : (
              <ConnectorDashboard
                onCreateNew={() => setShowConnectorWizard(true)}
                onEdit={(jobId) => {
                  // TODO: Implement edit functionality
                  console.log('Edit job:', jobId);
                }}
                onViewDetails={(jobId) => setSelectedJobId(jobId)}
              />
            )}
          </div>
        )}

        {activeTab === 'agents' && (
          <div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Agent System</h2>
              <p className="text-gray-600">Monitor and manage autonomous agents for document processing</p>
            </div>
            <AgentDashboard />
          </div>
        )}

        {activeTab === 'settings' && (
          <div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">Platform Settings</h2>
              <p className="text-gray-600">Configure system-wide settings, AI models, and security options</p>
            </div>
            <SettingsManager />
          </div>
        )}

        {activeTab === 'users' && (
          <div>
            <div className="mb-6">
              <h2 className="text-3xl font-bold text-gray-900">User Management</h2>
              <p className="text-gray-600">Manage user accounts, roles, and permissions</p>
            </div>
            <UserManager />
          </div>
        )}
      </main>
    </div>
  );
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
};

const AppContent: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return isAuthenticated() ? <Dashboard /> : <Login />;
};

export default App;